import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  ragione_sociale: "",
  piva: "",
  codice_fiscale: "",
  sede_legale: "",
  codice_ateco: "",
  codice_ateco_secondario: "",
  certificate_code: "",
  profile_level: "STANDARD",
  cluster_code: "CON-INFRA",
  year: new Date().getFullYear(),
  issued_at: "",
  expires_at: "",
  band: "V3",
  final_rating: 0,
  score_e: 0,
  score_s: 0,
  score_g: 0,
  verification_code: "",
  notes: "",
};

/**
 * Form per l'import retroattivo di certificati storici (admin only).
 * Bypassa la verifica ERP perché il certificato è già stato firmato in passato.
 */
export default function HistoricalImportTab() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit() {
    if (!form.ragione_sociale.trim() || !form.piva.trim() || !form.certificate_code.trim()) {
      toast.error("Ragione sociale, P.IVA e codice certificato sono obbligatori");
      return;
    }
    setSubmitting(true);
    setLastResult(null);
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        final_rating: Number(form.final_rating),
        score_e: form.score_e === "" ? null : Number(form.score_e),
        score_s: form.score_s === "" ? null : Number(form.score_s),
        score_g: form.score_g === "" ? null : Number(form.score_g),
        // Data ISO — se l'utente ha passato solo YYYY-MM-DD, aggiungiamo T00:00:00Z
        issued_at: form.issued_at.includes("T")
          ? form.issued_at
          : `${form.issued_at}T00:00:00+00:00`,
        expires_at: form.expires_at.includes("T")
          ? form.expires_at
          : `${form.expires_at}T23:59:59+00:00`,
      };
      const { data } = await api.post("/admin/import-historical-certificate", payload);
      setLastResult(data);
      toast.success(
        data.action === "created" ? "Certificato creato e pubblicato nel registro" : "Certificato aggiornato"
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore durante l'import");
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setForm(EMPTY);
    setLastResult(null);
  }

  return (
    <Card className="rounded-2xl border-slate-100 shadow-soft mt-4 overflow-hidden">
      <div className="p-5 border-b border-slate-100">
        <div className="font-semibold text-slate-900">Import Certificato Storico</div>
        <div className="text-slate-500 text-xs mt-1 max-w-3xl">
          Usa questo form per inserire retroattivamente nel registro pubblico un certificato già emesso in passato
          (es. template legacy, firma cartacea). Bypassa la verifica ERP. L&apos;operazione è idempotente sul codice certificato.
        </div>
      </div>

      <div className="p-5 space-y-5">
        <Section title="Identificazione azienda">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Ragione sociale *" value={form.ragione_sociale} onChange={set("ragione_sociale")} testid="hi-ragione" full />
            <F label="P.IVA *" value={form.piva} onChange={set("piva")} testid="hi-piva" />
            <F label="Codice fiscale" value={form.codice_fiscale} onChange={set("codice_fiscale")} testid="hi-cf" />
            <F label="Sede legale (una riga)" value={form.sede_legale} onChange={set("sede_legale")} testid="hi-sede" full />
            <F label="Codice ATECO primario" value={form.codice_ateco} onChange={set("codice_ateco")} testid="hi-ateco1" />
            <F label="Codice ATECO secondario" value={form.codice_ateco_secondario} onChange={set("codice_ateco_secondario")} testid="hi-ateco2" />
          </div>
        </Section>

        <Section title="Certificato">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Codice certificato *" value={form.certificate_code} onChange={set("certificate_code")} testid="hi-code" />
            <F label="Codice di verifica" value={form.verification_code} onChange={set("verification_code")} testid="hi-verify" />
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Profilo</Label>
              <Select value={form.profile_level} onValueChange={set("profile_level")}>
                <SelectTrigger data-testid="hi-profile" className="mt-1.5 h-10 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["BASE", "STANDARD", "ESTESO"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <F label="Cluster (es. CON-INFRA)" value={form.cluster_code} onChange={set("cluster_code")} testid="hi-cluster" />
            <F label="Anno assessment" value={form.year} onChange={set("year")} type="number" testid="hi-year" />
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Banda VIVA</Label>
              <Select value={form.band} onValueChange={set("band")}>
                <SelectTrigger data-testid="hi-band" className="mt-1.5 h-10 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["V1", "V2", "V3", "V4", "V5", "V6", "V7"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <F label="Data emissione (YYYY-MM-DD)" value={form.issued_at} onChange={set("issued_at")} testid="hi-issued" />
            <F label="Data scadenza (YYYY-MM-DD)" value={form.expires_at} onChange={set("expires_at")} testid="hi-expires" />
          </div>
        </Section>

        <Section title="Scoring finale">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <F label="Rating totale" value={form.final_rating} onChange={set("final_rating")} type="number" testid="hi-rating" />
            <F label="Score E" value={form.score_e} onChange={set("score_e")} type="number" testid="hi-e" />
            <F label="Score S" value={form.score_s} onChange={set("score_s")} type="number" testid="hi-s" />
            <F label="Score G" value={form.score_g} onChange={set("score_g")} type="number" testid="hi-g" />
          </div>
        </Section>

        <Section title="Note">
          <F label="" value={form.notes} onChange={set("notes")} testid="hi-notes" full />
        </Section>

        <div className="flex items-center gap-3 pt-2">
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="hi-submit"
            className="bg-viva-500 hover:bg-viva-600 text-white"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Importa nel registro
          </Button>
          <Button variant="ghost" onClick={reset} disabled={submitting}>Reset</Button>
          {lastResult && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 px-3 py-2 rounded-lg">
              <CheckCircle2 className="w-4 h-4" />
              <span className="font-medium">
                {lastResult.action === "created" ? "Creato" : "Aggiornato"} — cert {lastResult.certificate_code}
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.15em] text-viva-600 mb-3">{title}</div>
      {children}
    </div>
  );
}

function F({ label, value, onChange, type = "text", testid, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      {label && (
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      )}
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 h-10 rounded-lg border-slate-200 focus-visible:ring-viva-500/20 focus-visible:border-viva-500"
      />
    </div>
  );
}
